/* iter55z-c — Admin tier impersonation switcher.
 *
 * Rendered inside the AdminPage sidebar. Lets an admin flip their
 * effective subscription_tier between basic / pro / premier without
 * juggling multiple accounts, so all feature-gated flows can be
 * validated from a single login. Reload triggers on change so the
 * whole app re-fetches capabilities from the new server state.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { impersonateApi } from '@/lib/api';
import { toast } from 'sonner';
import { UserCog, Loader2 } from 'lucide-react';

const OPTIONS = [
  { value: '',        label: 'My real tier' },
  { value: 'basic',   label: 'Basic'  },
  { value: 'pro',     label: 'Pro Author' },
  { value: 'premier', label: 'Premier Studio' },
];

export default function TierImpersonationCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const d = await impersonateApi.get();
      setState(d);
    } catch {
      // 401 (cross-origin cookie not sent) or 403 (non-admin) — either
      // way the card should just hide instead of causing UI churn or
      // bouncing the whole admin console. The interceptor already
      // ignores 401s from this specific endpoint.
      setState({ hidden: true });
    }
  };
  useEffect(() => { load(); }, []);

  const change = async (tier) => {
    setBusy(true);
    try {
      if (!tier) await impersonateApi.clear();
      else       await impersonateApi.set(tier);
      toast.success(tier ? `Impersonating ${tier}. Reloading…` : 'Cleared. Reloading…');
      // Full reload picks up new capabilities everywhere (App.js reads
      // /auth/me on boot, and the app's capability guards derive from
      // subscription_tier via useCapabilities).
      window.setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change tier.');
      setBusy(false);
    }
  };

  if (!state || state.hidden) return null;

  const active = state.impersonated_tier || '';
  return (
    <div
      className="rounded-md border border-amber-300/60 bg-amber-50/70 p-2 shadow-panel"
      data-testid="admin-impersonate-card"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-900">
        <UserCog className="h-3.5 w-3.5" />
        View as tier
      </div>
      <p className="mb-2 text-[10.5px] leading-snug text-amber-900/80">
        Test feature-gating without a separate account. Effect is per-browser and clears on logout.
      </p>
      <div className="flex flex-wrap gap-1" data-testid="admin-impersonate-options">
        {OPTIONS.map((opt) => (
          <Button
            key={opt.value || 'clear'}
            variant={active === opt.value ? 'default' : 'outline'}
            size="sm"
            disabled={busy}
            onClick={() => change(opt.value)}
            data-testid={`admin-impersonate-btn-${opt.value || 'real'}`}
            className="h-6 px-2 text-[10.5px]"
          >
            {busy && active === opt.value ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {opt.label}
          </Button>
        ))}
      </div>
      {state.is_impersonating && (
        <div
          className="mt-2 rounded-sm bg-amber-200 px-2 py-1 text-[10.5px] font-semibold text-amber-900"
          data-testid="admin-impersonate-active-banner"
        >
          Impersonating <b>{state.effective_tier}</b> · real tier: {state.original_tier || 'none'}
        </div>
      )}
    </div>
  );
}
